import { Controller } from 'stimulus';
import Chart from 'chart.js/auto';
import { clipArea, unclipArea } from 'chart.js/helpers';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { hourLabels, seriesByHour } from './shared/labels';
import { currentLocale, t } from '../../javascript/lib/i18n';
import { currentTheme } from '../chart_controller/plugins/background_color_plugin';

const COLOR_BLACK = '#000';
const COLOR_GRAY = '#AAAFB5';
const COLOR_GRAY_LIGHT = '#E5E5E5';
const COLOR_GRAY_DARK = '#6A6A6A';

export default class extends Controller {
  static targets = ['canvas'];
  static values = { data: Object, url: String };

  initialize() {
    Chart.defaults.color = '#000';
  }

  disconnect() {
    this.chart.destroy();

    document.removeEventListener(
      'amba:theme_changed',
      this.reInitializeOnColorChange
    );
  }

  connect() {
    document.addEventListener(
      'amba:theme_changed',
      this.reInitializeOnColorChange
    );
    this.initializeChart();
  }

  reInitializeOnColorChange = () => {
    this.chart.destroy();
    this.initializeChart();
  };

  initializeChart() {
    const labels = hourLabels(currentLocale());

    const legendMargin = {
      id: 'legendMargin',
      beforeInit(chart, legend, options) {
        let fitValue = chart.legend.fit;
        chart.legend.fit = function fit() {
          fitValue.bind(chart.legend)();
          return (this.height += options.paddingTop);
        };
      },
      defaults: {
        paddingTop: 0,
      },
    };

    const scatterPlugin = {
      beforeDraw(chart) {
        const meta = chart.getDatasetMeta(2);

        meta.dataset.points.forEach((point) => (point.y = 65));
      },
    };

    const clipper = {
      beforeDatasetsDraw: function (chart) {
        const area = chart.chartArea;
        area.top = 50;
        clipArea(chart.ctx, area);
      },
      afterDatasetsDraw: function (chart) {
        unclipArea(chart.ctx);
      },
    };

    this.chart = new Chart(this.canvasTarget, {
      type: 'bar',
      plugins: [ChartDataLabels, legendMargin, scatterPlugin, clipper],
      options: {
        layout: {
          padding: {
            top: 20,
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            labels: {
              color: this.legendLabelColor,
            },
          },
          legendMargin: {
            paddingTop: 30,
          },
          tooltip: {
            position: 'average',
            filter: (tooltipItem, index, items) => {
              const totalYValues = items
                .map((item) => item.parsed.y)
                ?.reduce((sum, num) => sum + num);

              return totalYValues;
            },
            callbacks: {
              title: (data) => {
                if (data.length) {
                  const label = data[0].label;
                  const labelIndex = data[0].chart.data.labels.indexOf(label);
                  const nextHour =
                    data[0].chart.data.labels[labelIndex + 1] || labels[0];

                  return `${label} - ${nextHour}`;
                }
              },
              label: (context) => {
                if (context.parsed.y)
                  return `${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
        scales: {
          x: {
            steps: 1,
            beginAtZero: true,
            stacked: true,
            title: {
              display: true,
              text: t('js.charts.common.hour'),
              color: this.chartLabelColor,
            },
            grid: {
              color: this.gridColor,
              borderColor: this.gridColor,
              display: true,
            },
            ticks: {
              color: this.chartLabelColor,
            },
          },
          y: {
            max: this.maxYTicks,
            title: {
              display: true,
              text: t('js.charts.common.frequency'),
              color: this.chartLabelColor,
            },
            ticks: {
              precision: 0,
              color: this.chartLabelColor,
            },
            grid: {
              display: false,
              color: this.gridColor,
              borderColor: this.gridColor,
            },
          },
        },
      },
      data: {
        labels: labels,
        datasets: [
          {
            label: t('js.charts.sleep.waketimes'),
            backgroundColor: '#aacafa',
            order: 3,
            datalabels: {
              display: false,
            },
            data: seriesByHour(this.dataValue.waketimes),
          },
          {
            label: t('js.charts.sleep.bedtimes'),
            backgroundColor: '#0767f7',
            order: 2,
            datalabels: {
              display: false,
            },
            data: seriesByHour(this.dataValue.bedtimes),
          },
          {
            label: t('js.charts.sleep.out_of_bed'),
            type: 'scatter',
            backgroundColor: '#643bba',
            pointRadius: 12,
            pointHoverRadius: 10,
            animation: false,
            order: 1,
            datalabels: {
              color: '#fff',
              font: {
                weight: 'bold',
              },
              formatter: (value) => value,
            },
            data: seriesByHour(this.dataValue.out_of_bed),
          },
        ],
      },
    });
  }

  // We don't want the Y axis to account for out of bed values as they're always
  // rendered at the top. Instead force the max value of the Y axis by finding the
  // max number of waketimes or bedtimes
  get maxYTicks() {
    const allYValues = [
      ...this.dataValue.waketimes.map((item) => item.y),
      ...this.dataValue.bedtimes.map((item) => item.y),
    ];

    return Math.max(...allYValues);
  }

  get legendLabelColor() {
    return currentTheme() === 'light' ? COLOR_BLACK : COLOR_GRAY;
  }

  get chartLabelColor() {
    return currentTheme() === 'light' ? COLOR_GRAY_DARK : COLOR_GRAY;
  }

  get gridColor() {
    return currentTheme() === 'light' ? COLOR_GRAY_LIGHT : COLOR_GRAY_DARK;
  }
}
